import React, { useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useIntl, defineMessages } from "react-intl";
import { ListItemText, MenuItem, TextField, Input, Checkbox, MenuList } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { FormContext } from "./form";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import { ActivityFormField } from "./activityFormField";
import FormField from "./formFields";
import { getLocalizedNameWithIntl } from "../../globals/langHelpers";
import { WINTER_PERIOD_PREMIUM } from "../admin/skuList";

/*
    Form element for Complete job biling page - activity/SKU selection
    props:
        - activityOptions: object[], array of activity objects to pick from
        - field: string, field name for form data
*/

export const ActivitiesFormField = ({ activityOptions, billingReasons, field, billingReasonMandatory }) => {
    const formServices = useContext(FormContext);
    const [activities, setActivities] = useState(formServices.value[field]);
    const [selectedActivities, setSelectedActivities] = useState([]);
    const [activityCount, setActivityCount] = useState(0);
    const [search, setSearch] = useState("");
    const intl = useIntl();
    const removeUnderGroundCableReasons = useSelector((state) => state.reasons.partialReasons.get("CopperDecomBillingReason") ?? []);
    const removeUndergroundCabelSkus = useMemo(
        () => (process.env.REACT_APP_REMOVE_UNDERGROUND_CABLE_SKU || "").split(",").filter((sku) => !!sku.trim()),
        [process.env.REACT_APP_REMOVE_UNDERGROUND_CABLE_SKU],
    );

    const messages = defineMessages({
        addBillingActivityCaption: {
            id: "task-complete-form.add-billing-activity-caption",
            defaultMessage: "Add activity for billing",
            description: "Add billing activity caption",
        },
        additionalDetails: {
            id: "task-complete-form.additional-details",
            defaultMessage: "Additional details",
            description: "Additional details",
        },
        addButtonText: {
            id: "task-complete-form.add-button-text",
            defaultMessage: "Add",
            description: "Add button text",
        },
        filterActivities: {
            id: "task-complete-form.filter-activities",
            defaultMessage: "Filter activities…",
            description: "Filter activities…",
        },
    });

    const useStyles = makeStyles((theme) => ({
        container: {
            border: "1px solid #00549a",
            borderRadius: "2px",
            boxSizing: "border-box",
            padding: "16px",
        },
        picker: {
            height: "245px",
            overflowY: "scroll",
        },
        details: {
            paddingRight: "12px",
            color: "#999999",
        },
        detailsRequired: {
            color: "var(--text-error)",
        },
    }));

    const handleSearch = (event) => {
        setSearch(event.target.value.toString());
    };

    const onAdd = () => {
        const newActivities = activityOptions
            .filter((a) => a.selected)
            .map((a, index) => ({
                id: activityCount + index,
                productCode: a.productCode,
                productDescription_en: a.productDescription_en,
                productDescription_fr: a.productDescription_fr,
                quantity: 0,
                additionalDetails: "",
                unitOfMeasure: a.unitOfMeasure,
                isBillingReasonRequired: a.isBillingReasonRequired,
                billingReason: "",
            }));
        setActivities([...activities, ...newActivities]);
        formServices.value[field] = [...activities, ...newActivities];
        setActivityCount(activityCount + newActivities.length);
        activityOptions.forEach((a) => (a.selected = false));
        billingReasonMandatory(true);
    };

    const onCheck = (activity) => {
        activity.selected = !activity.selected;
        if (activity.selected) setSelectedActivities([...selectedActivities, activity]);
        else {
            var index = selectedActivities.find((a) => a === activity);
            if (index) {
                selectedActivities.splice(index, 1);
                setActivities([...activities]);
            }
        }
    };

    const handleRemove = (index) => {
        activities.splice(index, 1);
        setActivities([...activities]);
        formServices.value[field] = [...activities];
    };

    const verifyActivities = () => {
        const billingActivities = activities.filter((activity) => activity.isBillingReasonRequired);
        const billingReqActivities = billingActivities.filter(
            (activity) =>
                activity.isBillingReasonRequired &&
                activity.additionalDetails !== "" &&
                activity.billingReason.id &&
                parseFloat(activity.quantity) > 0,
        );
        const nonBillingReqActivities = activities.filter((activity) => !activity.isBillingReasonRequired && parseFloat(activity.quantity) > 0);
        billingReasonMandatory(validateActivities(billingReqActivities, nonBillingReqActivities));
    };

    const validateActivities = (billingActivites, nonBillingActivites) => {
        if (billingActivites.length > 0 && nonBillingActivites.length > 0) {
            return !(activities.length === billingActivites.length + nonBillingActivites.length);
        } else if (!billingActivites.length > 0 || nonBillingActivites.length > 0) {
            return !(activities.length === nonBillingActivites.length);
        } else if (billingActivites.length > 0 || !nonBillingActivites.length > 0) return !(activities.length === billingActivites.length);
        else return true;
    };

    const classes = useStyles();
    return (
        <>
            {activities &&
                activities.map((activity, index) => (
                    <ActivityFormField
                        key={activity.id}
                        activity={activity}
                        billingReasons={removeUndergroundCabelSkus.includes(activity.productCode) ? removeUnderGroundCableReasons : billingReasons}
                        label={getLocalizedNameWithIntl(intl, { object: activity, value: "productDescription" })}
                        editorProps={{
                            icon: (
                                <div>
                                    <span className={activity.isBillingReasonRequired ? classes.detailsRequired : classes.details}>
                                        {intl.formatMessage(messages.additionalDetails)}
                                    </span>
                                    <EditIcon />
                                </div>
                            ),
                            title: getLocalizedNameWithIntl(intl, { object: activity, value: "productDescription" }),
                            editor: (props) => (
                                <TextField
                                    id={"activity-details-" + activity.id}
                                    placeholder={intl.formatMessage(messages.additionalDetails)}
                                    multiline
                                    fullWidth
                                    rows={4}
                                    variant="outlined"
                                    InputProps={{
                                        inputProps: { maxLength: 300 },
                                    }}
                                    {...props}
                                />
                            ),
                        }}
                        required={activity.isBillingReasonRequired}
                        onRemove={() => handleRemove(index)}
                        characterCountEnabled
                        verifyActivities={verifyActivities}
                    />
                ))}
            <FormField
                field="activities"
                label={intl.formatMessage(messages.addBillingActivityCaption)}
                displayValue={false}
                editorProps={{
                    icon: <AddIcon color="primary" />,
                    title: intl.formatMessage(messages.addBillingActivityCaption),
                    editor: (props) => (
                        <>
                            <div className={classes.container}>
                                <Input
                                    fullWidth
                                    placeholder={intl.formatMessage(messages.filterActivities)}
                                    value={search}
                                    type="search"
                                    onChange={handleSearch}
                                />
                                <MenuList className={"bell-scrollbar " + classes.picker}>
                                    {activityOptions
                                        .filter(
                                            (a) =>
                                                a.productCode != WINTER_PERIOD_PREMIUM &&
                                                getLocalizedNameWithIntl(intl, { object: a, value: "productDescription" })
                                                    .toLowerCase()
                                                    .includes(search.toLowerCase()),
                                        )
                                        .map((activity) => (
                                            <MenuItem style={{ whiteSpace: "normal" }} key={activity.id} value={activity.id} disableGutters>
                                                <Checkbox
                                                    onChange={() => onCheck(activity)}
                                                    value={activity.id}
                                                    color="primary"
                                                    checked={activity.selected}
                                                />
                                                <ListItemText
                                                    disableTypography
                                                    primary={getLocalizedNameWithIntl(intl, { object: activity, value: "productDescription" })}
                                                />
                                            </MenuItem>
                                        ))}
                                </MenuList>
                            </div>
                        </>
                    ),
                    btnText: intl.formatMessage(messages.addButtonText),
                    btnCallback: onAdd,
                }}
            />
        </>
    );
};

export default ActivitiesFormField;
